/** The column under the middle of the view, and how far across it the middle falls, from 0 to 1. */
export interface GridPoint {
  sprintId: string;
  at: number;
}

/** Kept for the session only: it is where the reader was a tab ago, not a preference. */
const inView = new Map<string, GridPoint>();

export const rememberPointInView = (gridId: string, point: GridPoint): void => {
  inView.set(gridId, point);
};

export const pointInView = (gridId: string): GridPoint | undefined => inView.get(gridId);

export const forgetPointsInView = (): void => inView.clear();

const measure = (el: HTMLElement): { middle: number; heads: HTMLElement[]; frozen: number } | undefined => {
  const stuck = el.querySelector<HTMLElement>('[role="columnheader"]');
  if (!stuck) return undefined;
  const frozen = stuck.getBoundingClientRect().width;
  const middle = el.getBoundingClientRect().left + frozen + (el.clientWidth - frozen) / 2;
  return { middle, frozen, heads: [...el.querySelectorAll<HTMLElement>('[data-sprint]')] };
};

/** Read off the columns' `data-sprint`, past the name column that sticks over them. */
export const pointAtCentre = (el: HTMLElement): GridPoint | undefined => {
  const view = measure(el);
  if (!view) return undefined;
  const hit = view.heads.find(head => {
    const cell = head.getBoundingClientRect();
    return cell.left <= view.middle && view.middle < cell.right;
  });
  if (!hit?.dataset.sprint) return undefined;
  const cell = hit.getBoundingClientRect();
  return { sprintId: hit.dataset.sprint, at: (view.middle - cell.left) / cell.width };
};

/** The scrollLeft that puts the point back in the middle, or nothing where its sprint has no column here. */
export const scrollToPoint = (el: HTMLElement, { sprintId, at }: GridPoint): number | undefined => {
  const view = measure(el);
  const target = view?.heads.find(head => head.dataset.sprint === sprintId);
  if (!view || !target) return undefined;
  const cell = target.getBoundingClientRect();
  // Past either end is not a scroll position; the browser clamps it to one.
  return el.scrollLeft + cell.left + at * cell.width - view.middle;
};
